import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { CartItem } from './cartSlice';
import { RootState } from './types';
import type { CustomerOrderSummary } from '../api/orders';
import { compareCustomers, customerKey } from '../utils/customerPicker';
export interface OrdersSliceObject {
  completed: CompletedOrdersObject;
  filteredCompleted: CompletedOrdersObject;
  pending: PendingOrdersObject;
  filteredPending: PendingOrdersObject;
  partial: PartialOrdersObject;
  filteredPartial: PartialOrdersObject;
  topCustomers: TopCustomer[];
  cloudCustomerSummaries: CustomerOrderSummary[];
}

export interface CompletedOrdersObject {
  docs: OrderDoc[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
}
export interface PartialOrdersObject {
  docs: OrderDoc[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
}
export interface PendingOrdersObject {
  previousDocs: CartItem[];
  docs: OrderDoc[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
}
export interface SalesProductInformation {
  bp_order_completed: boolean;
  bp_order_created_at: string;
  bp_order_price: number;
  bp_order_quantity: number;
  bp_order_updated_at: string;
  bp_product_information: ProductInformation;
  bp_sales_id: string;
  bp_selling_price_changed?: boolean;
}
export interface ProductInformation {
  _id: string;
  bp_owner_id: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number | null;
  bp_product_selling_price: number | null;
  bp_product_quantity: number | null;
  /** Missing on lines saved before services existed: a product. */
  bp_product_kind?: 'product' | 'service';
  bp_product_image?: string;
  bp_variant_id?: string;
  bp_variant_attributes?: Record<string, string>;
  bp_product_has_variants?: boolean;
  bp_product_variant_attributes?: string[];
  bp_product_parent_name?: string;
  // Add any other fields here if necessary
}

export interface AssignedStaffInformation {
  _id: string;
  bp_employee_name: string;
  bp_employee_role: string;
}

export interface OrderDoc {
  bp_order_customer_information: string;
  /** The linked back-office customer and their phone, from the server. */
  bp_order_customer_id?: string | null;
  bp_order_customer_phone_number?: string | null;
  bp_order_assigned_staff_information?: AssignedStaffInformation | null;
  bp_order_id: string;
  bp_order_master_created_at: string;
  bp_order_master_order_status: string;
  bp_order_master_receipt_reference_id: string;
  bp_order_master_payment_information: PaymentInformation;
  bp_order_master_updated_at: string;
  bp_order_master_employee_id?: string;
  /** Creator display name from stream/API (`buildCanonicalEmployeeOrderDocument`). */
  bp_order_master_employee_information?: string | null;
  bp_order_master_employee_role?: string;
  bp_sales_products_information: SalesProductInformation[];
  bp_total_sales_products: number;
}
export interface TopCustomer {
  name: string;
  count: number;
  customerId?: string | null;
  isCloud?: boolean;
}
export interface PaymentInformation {
  _id: string;
  bp_payment_status: boolean;
  bp_payment_amount_due: number;
  bp_payment_amount: number;
  bp_payment_amount_refunded: number;
  bp_order_information_id: string;
  bp_payment_created_at: string;
  bp_payment_updated_at: string;
  __v: number;
  bp_payment_already_paid_amount: number;
  bp_payment_method: string;
}
const initialCompletedOrders: CompletedOrdersObject = {
  docs: [],
  hasNextPage: true,
  hasPrevPage: true,
  limit: 12,
  nextPage: 1,
  page: 0,
  perPage: 12,
  totalDocs: 0,
  totalPages: 0,
};
const initialPendingOrders: PendingOrdersObject = {
  //order just before Adding more products
  previousDocs: [],
  docs: [],
  hasNextPage: true,
  hasPrevPage: true,
  limit: 12,
  nextPage: 1,
  page: 0,
  perPage: 12,
  totalDocs: 0,
  totalPages: 0,
};
const initialPartialOrders: PartialOrdersObject = {
  docs: [],
  hasNextPage: true,
  hasPrevPage: true,
  limit: 12,
  nextPage: 1,
  page: 0,
  perPage: 12,
  totalDocs: 0,
  totalPages: 0,
};

const initialState: OrdersSliceObject = {
  completed: initialCompletedOrders,
  filteredCompleted: initialCompletedOrders,
  pending: initialPendingOrders,
  filteredPending: initialPendingOrders,
  partial: initialPartialOrders,
  filteredPartial: initialPartialOrders,
  topCustomers: [],
  cloudCustomerSummaries: [],
};

// Helpers for precomputing frequent customers
// Who a typed order name refers to. Trimmed, never lowercased: "moses" and
// "Moses" are counted apart here because the server links their orders apart.
const normalizeCustomerKey = customerKey;

const computeTopCustomers = (
  docs: OrderDoc[],
  limit: number = 100,
): TopCustomer[] => {
  const counts = new Map<string, { name: string; count: number }>();
  for (const order of docs) {
    const raw = (order?.bp_order_customer_information || '').trim();
    if (!raw) continue;
    const key = normalizeCustomerKey(raw);
    if (!key) continue;
    const prev = counts.get(key);
    if (prev) {
      prev.count += 1;
    } else {
      counts.set(key, { name: raw, count: 1 });
    }
  }
  return Array.from(counts.values()).sort(compareCustomers).slice(0, limit);
};

// Local-first helpers
const toLower = (value: string | undefined | null): string =>
  String(value || '')
    .trim()
    .toLowerCase();

const filterOrdersByTerm = (orders: OrderDoc[], term: string): OrderDoc[] => {
  const q = toLower(term);
  if (!q) return [];
  return orders.filter(order => {
    const receiptId = toLower(order?.bp_order_master_receipt_reference_id);
    const customer = toLower(order?.bp_order_customer_information);
    return receiptId.includes(q) || customer.includes(q);
  });
};

const normalizePrimaryBucketMeta = (
  bucket: CompletedOrdersObject | PendingOrdersObject | PartialOrdersObject,
) => {
  const perPage = Math.max(1, Number(bucket.perPage || bucket.limit || 12));
  const page = Math.max(1, Number(bucket.page || 1));
  const totalDocs = Math.max(0, Number(bucket.totalDocs || 0));
  const totalPages = Math.max(1, Math.ceil(totalDocs / perPage));

  bucket.perPage = perPage;
  bucket.limit = Math.max(1, Number(bucket.limit || perPage));
  bucket.page = page;
  bucket.totalDocs = totalDocs;
  bucket.totalPages = totalPages;
  bucket.hasPrevPage = page > 1;
  bucket.hasNextPage = page < totalPages;
  bucket.nextPage = bucket.hasNextPage ? page + 1 : page;
};

/** A page of orders for one status bucket is proof those ids are no longer
 * in the other two, so bulk merges evict them there in the same pass. */
const evictIds = (docs: OrderDoc[], incoming: OrderDoc[]): OrderDoc[] => {
  const ids = new Set(incoming.map(o => o.bp_order_id));
  return docs.filter(o => !ids.has(o.bp_order_id));
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    addCompletedOrder: (
      state,
      action: PayloadAction<CompletedOrdersObject>,
    ) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addCompletedOrder');
        return state;
      }

      const existingOrders = [...state.completed.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Process new orders first to ensure API response values take precedence
      // Then merge with existing orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const existingOrder = orderMap.get(order.bp_order_id);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >=
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          // Deep copy to preserve all nested fields including bp_selling_price_changed
          orderMap.set(order.bp_order_id, JSON.parse(JSON.stringify(order)));
        }
      });

      // Convert map back to array and sort
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      const pendingDocs = evictIds(state.pending.docs, newOrders);
      const partialDocs = evictIds(state.partial.docs, newOrders);

      // Update state
      return {
        ...state,
        completed: {
          ...state.completed,
          ...action.payload,
          docs: mergedOrders,
        },
        pending: { ...state.pending, docs: pendingDocs },
        partial: { ...state.partial, docs: partialDocs },
        topCustomers: computeTopCustomers(
          [...mergedOrders, ...pendingDocs, ...partialDocs],
          200,
        ),
      };
    },
    addFilteredCompletedOrder: (
      state,
      action: PayloadAction<CompletedOrdersObject>,
    ) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addFilteredCompletedOrder');
        return state;
      }

      const existingOrders = [...state.filteredCompleted.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Create a unique key combining both IDs to prevent duplications
      const createUniqueKey = (order: OrderDoc) =>
        `${order.bp_order_id}_${order.bp_order_master_receipt_reference_id}`;

      // Merge existing and new orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const uniqueKey = createUniqueKey(order);
        const existingOrder = orderMap.get(uniqueKey);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          orderMap.set(uniqueKey, { ...order });
        }
      });

      // Convert map back to array and sort by update date
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      return {
        ...state,
        filteredCompleted: {
          ...state.filteredCompleted,
          ...action.payload,
          docs: mergedOrders,
        },
        topCustomers: computeTopCustomers(
          [
            ...state.completed.docs,
            ...state.pending.docs,
            ...state.partial.docs,
          ],
          200,
        ),
      };
    },
    clearFilteredCompletedOrder: state => {
      state.filteredCompleted = initialCompletedOrders;
    },
    addCompletedOrderItem: (state, action: PayloadAction<OrderDoc>) => {
      if (!action.payload) {
        console.error(
          'Invalid payload in addCompletedOrderItem',
          action.payload,
        );
        return state;
      }

      state.completed.docs.push(action.payload);
      state.topCustomers = computeTopCustomers(
        [...state.completed.docs, ...state.pending.docs, ...state.partial.docs],
        200,
      );
    },

    clearCompletedOrders: state => {
      state.completed = initialCompletedOrders;
    },

    //PENDING ORDERS
    addpending: (state, action: PayloadAction<PendingOrdersObject>) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addpending');
        return state;
      }

      const existingOrders = [...state.pending.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Process new orders first to ensure API response values take precedence
      // Then merge with existing orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const existingOrder = orderMap.get(order.bp_order_id);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >=
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          // Deep copy to preserve all nested fields including bp_selling_price_changed
          orderMap.set(order.bp_order_id, JSON.parse(JSON.stringify(order)));
        }
      });

      // Convert map back to array and sort
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      const completedDocs = evictIds(state.completed.docs, newOrders);
      const partialDocs = evictIds(state.partial.docs, newOrders);

      // Update state
      return {
        ...state,
        pending: {
          ...state.pending,
          ...action.payload,
          docs: mergedOrders,
        },
        completed: { ...state.completed, docs: completedDocs },
        partial: { ...state.partial, docs: partialDocs },
        topCustomers: computeTopCustomers(
          [...completedDocs, ...mergedOrders, ...partialDocs],
          200,
        ),
      };
    },
    addFilteredPendingOrder: (
      state,
      action: PayloadAction<PendingOrdersObject>,
    ) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addFilteredPendingOrder');
        return state;
      }

      const existingOrders = [...state.filteredPending.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Create a unique key combining both IDs to prevent duplications
      const createUniqueKey = (order: OrderDoc) =>
        `${order.bp_order_id}_${order.bp_order_master_receipt_reference_id}`;

      // Merge existing and new orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const uniqueKey = createUniqueKey(order);
        const existingOrder = orderMap.get(uniqueKey);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          orderMap.set(uniqueKey, { ...order });
        }
      });

      // Convert map back to array and sort by update date
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      return {
        ...state,
        filteredPending: {
          ...state.filteredPending,
          ...action.payload,
          docs: mergedOrders,
        },
        topCustomers: computeTopCustomers(
          [
            ...state.completed.docs,
            ...state.pending.docs,
            ...state.partial.docs,
          ],
          200,
        ),
      };
    },
    clearFilteredPendingOrder: state => {
      state.filteredPending = initialPendingOrders;
    },
    addPendingOrderItem: (state, action: PayloadAction<OrderDoc>) => {
      if (!action.payload) {
        console.error('Invalid payload in addPendingOrderItem', action.payload);
        return state;
      }

      state.pending.docs.push(action.payload);
      state.topCustomers = computeTopCustomers(
        [...state.completed.docs, ...state.pending.docs, ...state.partial.docs],
        200,
      );
    },
    addPendingorderDocs: (state, action: PayloadAction<OrderDoc[]>) => {
      state.pending.docs.push(...action.payload);
    },
    addPreviouspendingOrder: (state, action: PayloadAction<CartItem[]>) => {
      state.pending.previousDocs = action.payload;
    },
    removePendingOrder: (state, action: PayloadAction<OrderDoc>) => {
      state.pending.docs = state.pending.docs.filter(
        order => order.bp_order_id !== action.payload.bp_order_id,
      );
    },
    clearPendingOrders: state => {
      state.pending = initialPendingOrders;
    },

    //PARTIAL ORDERS
    addPartialOrder: (state, action: PayloadAction<PartialOrdersObject>) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addPartialOrder');
        return state;
      }

      const existingOrders = [...state.partial.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Process new orders first to ensure API response values take precedence
      // Then merge with existing orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const existingOrder = orderMap.get(order.bp_order_id);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >=
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          // Deep copy to preserve all nested fields including bp_selling_price_changed
          orderMap.set(order.bp_order_id, JSON.parse(JSON.stringify(order)));
        }
      });

      // Convert map back to array and sort
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      const completedDocs = evictIds(state.completed.docs, newOrders);
      const pendingDocs = evictIds(state.pending.docs, newOrders);

      // Update state
      return {
        ...state,
        partial: {
          ...state.partial,
          ...action.payload,
          docs: mergedOrders,
        },
        completed: { ...state.completed, docs: completedDocs },
        pending: { ...state.pending, docs: pendingDocs },
        topCustomers: computeTopCustomers(
          [...completedDocs, ...pendingDocs, ...mergedOrders],
          200,
        ),
      };
    },
    addFilteredPartialOrder: (
      state,
      action: PayloadAction<PartialOrdersObject>,
    ) => {
      if (!action.payload || !Array.isArray(action.payload.docs)) {
        console.error('Invalid payload in addFilteredPartialOrder');
        return state;
      }

      const existingOrders = [...state.filteredPartial.docs];
      const newOrders = action.payload.docs;
      const orderMap = new Map<string, OrderDoc>();

      // Create a unique key combining both IDs to prevent duplications
      const createUniqueKey = (order: OrderDoc) =>
        `${order.bp_order_id}_${order.bp_order_master_receipt_reference_id}`;

      // Merge existing and new orders, keeping the most recent version
      [...newOrders, ...existingOrders].forEach(order => {
        const uniqueKey = createUniqueKey(order);
        const existingOrder = orderMap.get(uniqueKey);
        if (
          !existingOrder ||
          new Date(order.bp_order_master_updated_at) >
            new Date(existingOrder.bp_order_master_updated_at)
        ) {
          orderMap.set(uniqueKey, { ...order });
        }
      });

      // Convert map back to array and sort by update date
      const mergedOrders = Array.from(orderMap.values()).sort(
        (a, b) =>
          new Date(b.bp_order_master_updated_at).getTime() -
          new Date(a.bp_order_master_updated_at).getTime(),
      );

      return {
        ...state,
        filteredPartial: {
          ...state.filteredPartial,
          ...action.payload,
          docs: mergedOrders,
        },
        topCustomers: computeTopCustomers(
          [
            ...state.completed.docs,
            ...state.pending.docs,
            ...state.partial.docs,
          ],
          200,
        ),
      };
    },
    addPartialOrderItem: (state, action: PayloadAction<OrderDoc>) => {
      if (!action.payload) {
        console.error('Invalid payload in addPartialOrderItem', action.payload);
        return state;
      }

      state.partial.docs.push(action.payload);
      state.topCustomers = computeTopCustomers(
        [...state.completed.docs, ...state.pending.docs, ...state.partial.docs],
        200,
      );
    },
    clearFilteredPartialOrder: state => {
      state.filteredPartial = initialPartialOrders;
    },
    addPartialOrderDocs: (state, action: PayloadAction<OrderDoc[]>) => {
      state.partial.docs.push(...action.payload);
    },
    removePartialOrder: (state, action: PayloadAction<OrderDoc>) => {
      state.partial.docs = state.partial.docs.filter(
        order => order.bp_order_id !== action.payload.bp_order_id,
      );
    },
    clearPartialOrders: state => {
      state.partial = initialPartialOrders;
    },

    // Local-first search seeders (show immediate results from in-memory docs)
    seedFilteredCompletedFromLocal: (state, action: PayloadAction<string>) => {
      const term = action.payload || '';
      const matches = filterOrdersByTerm(state.completed.docs, term);
      state.filteredCompleted = {
        ...state.filteredCompleted,
        docs: matches,
        page: 1,
        nextPage: 2,
        totalDocs: matches.length,
        hasNextPage: true,
      } as CompletedOrdersObject;
    },
    seedFilteredPendingFromLocal: (state, action: PayloadAction<string>) => {
      const term = action.payload || '';
      const matches = filterOrdersByTerm(state.pending.docs, term);
      state.filteredPending = {
        ...state.filteredPending,
        docs: matches,
        page: 1,
        nextPage: 2,
        totalDocs: matches.length,
        hasNextPage: true,
      } as PendingOrdersObject;
    },
    seedFilteredPartialFromLocal: (state, action: PayloadAction<string>) => {
      const term = action.payload || '';
      const matches = filterOrdersByTerm(state.partial.docs, term);
      state.filteredPartial = {
        ...state.filteredPartial,
        docs: matches,
        page: 1,
        nextPage: 2,
        totalDocs: matches.length,
        hasNextPage: true,
      } as PartialOrdersObject;
    },

    // Upsert a single order doc and move it to the correct status bucket.
    // This is used when an order is edited from backoffice so the employee
    // orders tabs (Completed / Pending / Partial) stay in sync.
    upsertOrderDoc: (state, action: PayloadAction<OrderDoc>) => {
      const updated = action.payload;
      if (!updated || !updated.bp_order_id) {
        console.error('Invalid payload in upsertOrderDoc', updated);
        return state;
      }

      const statusLower = String(
        updated.bp_order_master_order_status || '',
      ).toLowerCase();
      const bucketKeys: Array<'completed' | 'pending' | 'partial'> = [
        'completed',
        'pending',
        'partial',
      ];
      const wasInBucket = {
        completed: state.completed.docs.some(
          o => o.bp_order_id === updated.bp_order_id,
        ),
        pending: state.pending.docs.some(
          o => o.bp_order_id === updated.bp_order_id,
        ),
        partial: state.partial.docs.some(
          o => o.bp_order_id === updated.bp_order_id,
        ),
      };

      const removeById = (arr: OrderDoc[]) =>
        arr.filter(o => o.bp_order_id !== updated.bp_order_id);

      // Remove from all primary buckets first
      state.completed.docs = removeById(state.completed.docs);
      state.pending.docs = removeById(state.pending.docs);
      state.partial.docs = removeById(state.partial.docs);

      // Decide target bucket based on status
      const targetKey: 'completed' | 'pending' | 'partial' =
        statusLower.includes('partial')
          ? 'partial'
          : statusLower === 'completed'
            ? 'completed'
            : 'pending';

      // Merge into target bucket and keep sorted / de-duped
      const mergeInto = (existing: OrderDoc[]): OrderDoc[] => {
        const orderMap = new Map<string, OrderDoc>();
        [...existing, updated].forEach(order => {
          const prev = orderMap.get(order.bp_order_id);
          if (
            !prev ||
            new Date(order.bp_order_master_updated_at).getTime() >
              new Date(prev.bp_order_master_updated_at).getTime()
          ) {
            orderMap.set(order.bp_order_id, { ...order });
          }
        });
        return Array.from(orderMap.values()).sort(
          (a, b) =>
            new Date(b.bp_order_master_updated_at).getTime() -
            new Date(a.bp_order_master_updated_at).getTime(),
        );
      };

      state[targetKey].docs = mergeInto(state[targetKey].docs);
      const wasInTargetBefore = wasInBucket[targetKey];
      const wasInAnyBefore =
        wasInBucket.completed || wasInBucket.pending || wasInBucket.partial;

      if (!wasInTargetBefore) {
        state[targetKey].totalDocs += 1;
      }

      if (wasInAnyBefore) {
        bucketKeys.forEach(key => {
          if (key !== targetKey && wasInBucket[key]) {
            state[key].totalDocs = Math.max(0, state[key].totalDocs - 1);
          }
        });
      }

      normalizePrimaryBucketMeta(state.completed);
      normalizePrimaryBucketMeta(state.pending);
      normalizePrimaryBucketMeta(state.partial);

      // Recompute top customers so dashboards stay consistent
      state.topCustomers = computeTopCustomers(
        [...state.completed.docs, ...state.pending.docs, ...state.partial.docs],
        200,
      );
    },
    removeOrderById: (state, action: PayloadAction<string>) => {
      const orderId = String(action.payload || '').trim();
      if (!orderId) return state;

      const removeById = (arr: OrderDoc[]) =>
        arr.filter(o => o.bp_order_id !== orderId);

      const removedFrom = {
        completed: state.completed.docs.some(o => o.bp_order_id === orderId),
        pending: state.pending.docs.some(o => o.bp_order_id === orderId),
        partial: state.partial.docs.some(o => o.bp_order_id === orderId),
      };

      state.completed.docs = removeById(state.completed.docs);
      state.pending.docs = removeById(state.pending.docs);
      state.partial.docs = removeById(state.partial.docs);

      if (removedFrom.completed) {
        state.completed.totalDocs = Math.max(0, state.completed.totalDocs - 1);
      }
      if (removedFrom.pending) {
        state.pending.totalDocs = Math.max(0, state.pending.totalDocs - 1);
      }
      if (removedFrom.partial) {
        state.partial.totalDocs = Math.max(0, state.partial.totalDocs - 1);
      }

      normalizePrimaryBucketMeta(state.completed);
      normalizePrimaryBucketMeta(state.pending);
      normalizePrimaryBucketMeta(state.partial);

      state.topCustomers = computeTopCustomers(
        [...state.completed.docs, ...state.pending.docs, ...state.partial.docs],
        200,
      );
    },
    setCloudCustomerSummaries: (
      state,
      action: PayloadAction<CustomerOrderSummary[]>,
    ) => {
      state.cloudCustomerSummaries = action.payload;
    },
    /** A customer just created (or found) from the Save Order sheet, so the
     * list shows them at once; one already listed keeps its order count. */
    upsertCloudCustomerSummary: (
      state,
      action: PayloadAction<CustomerOrderSummary>,
    ) => {
      const id = String(action.payload?._id || '');
      if (!id) return;
      const index = state.cloudCustomerSummaries.findIndex(
        c => String(c._id) === id,
      );
      if (index !== -1) {
        state.cloudCustomerSummaries[index] = {
          ...state.cloudCustomerSummaries[index],
          bp_customer_name: action.payload.bp_customer_name,
        };
        return;
      }
      state.cloudCustomerSummaries.unshift(action.payload);
    },
    clearCloudCustomerSummaries: state => {
      state.cloudCustomerSummaries = [];
    },
    /** Names counted from the order lists; the order clears leave them. */
    clearTopCustomers: state => {
      state.topCustomers = [];
    },
  },
});

export const {
  addCompletedOrder,
  addCompletedOrderItem,
  clearCompletedOrders,
  addpending,
  addPendingorderDocs,
  addPreviouspendingOrder,
  removePendingOrder,
  clearPendingOrders,
  addPartialOrder,
  addPartialOrderDocs,
  clearPartialOrders,
  removePartialOrder,
  addFilteredCompletedOrder,
  clearFilteredCompletedOrder,
  addFilteredPendingOrder,
  clearFilteredPendingOrder,
  addFilteredPartialOrder,
  clearFilteredPartialOrder,
  seedFilteredCompletedFromLocal,
  seedFilteredPendingFromLocal,
  seedFilteredPartialFromLocal,
  addPartialOrderItem,
  addPendingOrderItem,
  upsertOrderDoc,
  removeOrderById,
  setCloudCustomerSummaries,
  upsertCloudCustomerSummary,
  clearCloudCustomerSummaries,
  clearTopCustomers,
} = ordersSlice.actions;
export default ordersSlice.reducer;

const NO_ORDER_EDIT_SNAPSHOT: CartItem[] = [];

// Lines of the order being edited, as they were when editing began. The
// snapshot is never cleared when editing ends, so outside edit mode it is
// stale and must not lend its paid figures to lines of a new sale.
export const selectOrderEditSnapshot = (state: RootState): CartItem[] =>
  state.cart.updatingCartitems
    ? (state.orders.pending.previousDocs ?? NO_ORDER_EDIT_SNAPSHOT)
    : NO_ORDER_EDIT_SNAPSHOT;

// Basic memoized selectors
export const selectCompletedOrders = createSelector(
  [(state: RootState) => state.orders.completed],
  completedOrders => completedOrders,
);

export const selectFilteredCompletedOrders = createSelector(
  [(state: RootState) => state.orders.filteredCompleted],
  filteredCompletedOrders => filteredCompletedOrders,
);

export const selectPendingOrders = createSelector(
  [(state: RootState) => state.orders.pending],
  pendingOrders => pendingOrders,
);

export const selectFilteredPendingOrders = createSelector(
  [(state: RootState) => state.orders.filteredPending],
  filteredPendingOrders => filteredPendingOrders,
);

export const selectPartialOrders = createSelector(
  [(state: RootState) => state.orders.partial],
  partialOrders => partialOrders,
);

export const selectFilteredPartialOrders = createSelector(
  [(state: RootState) => state.orders.filteredPartial],
  filteredPartialOrders => filteredPartialOrders,
);

// Memoized order docs
export const selectCompletedOrderDocs = createSelector(
  [selectCompletedOrders],
  completedOrders => completedOrders.docs,
);

export const selectFilteredCompletedOrderDocs = createSelector(
  [selectFilteredCompletedOrders],
  filteredCompletedOrders => filteredCompletedOrders.docs,
);

export const selectPendingOrderDocs = createSelector(
  [selectPendingOrders],
  pendingOrders => pendingOrders.docs,
);

export const selectFilteredPendingOrderDocs = createSelector(
  [selectFilteredPendingOrders],
  filteredPendingOrders => filteredPendingOrders.docs,
);

export const selectPartialOrderDocs = createSelector(
  [selectPartialOrders],
  partialOrders => partialOrders.docs,
);

export const selectFilteredPartialOrderDocs = createSelector(
  [selectFilteredPartialOrders],
  filteredPartialOrders => filteredPartialOrders.docs,
);

// Memoized order statistics
export const selectCompletedOrdersStats = createSelector(
  [selectCompletedOrderDocs],
  orders => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => {
      return sum + (order.bp_total_sales_products || 0);
    }, 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
    };
  },
);

export const selectPendingOrdersStats = createSelector(
  [selectPendingOrderDocs],
  orders => {
    const totalOrders = orders.length;
    const totalValue = orders.reduce((sum, order) => {
      return sum + (order.bp_total_sales_products || 0);
    }, 0);

    return {
      totalOrders,
      totalValue,
    };
  },
);

export const selectPartialOrdersStats = createSelector(
  [selectPartialOrderDocs],
  orders => {
    const totalOrders = orders.length;
    const totalValue = orders.reduce((sum, order) => {
      return sum + (order.bp_total_sales_products || 0);
    }, 0);

    return {
      totalOrders,
      totalValue,
    };
  },
);

// Memoized orders by date range
export const selectOrdersByDateRange = createSelector(
  [
    selectCompletedOrderDocs,
    (state: RootState, startDate: Date, endDate: Date) => ({
      startDate,
      endDate,
    }),
  ],
  (orders, { startDate, endDate }) => {
    return orders.filter(order => {
      const orderDate = new Date(order.bp_order_master_created_at);
      return orderDate >= startDate && orderDate <= endDate;
    });
  },
);

// Memoized orders by payment status
export const selectOrdersByPaymentStatus = createSelector(
  [
    selectCompletedOrderDocs,
    (state: RootState, paymentStatus: boolean) => paymentStatus,
  ],
  (orders, paymentStatus) => {
    return orders.filter(order => {
      const paymentInfo = order.bp_order_master_payment_information;
      return paymentInfo?.bp_payment_status === paymentStatus;
    });
  },
);

// Memoized recent orders (last 7 days)
export const selectRecentOrders = createSelector(
  [selectCompletedOrderDocs],
  orders => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return orders.filter(order => {
      const orderDate = new Date(order.bp_order_master_created_at);
      return orderDate >= sevenDaysAgo;
    });
  },
);

// Memoized selector for top customers (already maintained ahead-of-time in state)
export const selectTopCustomers = createSelector(
  [(state: RootState) => state.orders.topCustomers],
  topCustomers => topCustomers,
);

// Merged cloud CRM customers + local frequency customers.
// Cloud entries are authoritative on count and carry the CRM _id.
// Local-only entries (names typed on orders but not in CRM) kept as fallback.
export const selectMergedCustomers = createSelector(
  [
    (state: RootState) => state.orders.topCustomers,
    (state: RootState) => state.orders.cloudCustomerSummaries,
  ],
  (localCustomers, cloudSummaries): TopCustomer[] => {
    const merged = new Map<string, TopCustomer>();

    for (const c of cloudSummaries) {
      const key = normalizeCustomerKey(c.bp_customer_name);
      if (!key) continue;
      merged.set(key, {
        name: c.bp_customer_name,
        count: c.bp_order_count || 0,
        customerId: c._id,
        isCloud: true,
      });
    }

    for (const l of localCustomers) {
      const key = normalizeCustomerKey(l.name);
      if (!key) continue;
      if (!merged.has(key)) {
        merged.set(key, {
          name: l.name,
          count: l.count,
          customerId: null,
          isCloud: false,
        });
      }
    }

    return Array.from(merged.values()).sort(compareCustomers);
  },
);
